/**
 * Qualities, not chapters.
 *
 * A set of drills does not always sort into a line. These six two-foot
 * finishing games sort into four qualities a coach is trying to build, and
 * some of them build more than one: the pump fake game is both a stationary
 * finish and a timing game, and the two introductory games are partly
 * stationary too. A linear reader forces you to pick one home for each drill
 * and lose the rest of the truth.
 *
 * So: a tab per quality, a sentence or two saying what the quality is, and
 * the drills that build it. A drill can appear under several.
 *
 * Two things follow from a drill appearing more than once.
 *
 *   The second time you meet it, it should say so. A drill already watched
 *   comes back dimmed and marked, so it reads as "this one is here again
 *   because it also builds this" rather than as something new you missed.
 *
 *   And it should still move. A dimmed static thumbnail reads as disabled,
 *   which is the opposite of the point, so a watched card plays its Mux
 *   animated preview instead. You can see what it is without opening it.
 *
 * Intensity is one to three, and it is the intensity of the challenge rather
 * than a difficulty rating. A coach picking a game for a Tuesday needs to
 * know what it will cost the group, not how advanced it makes them look.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Play, Check, ChevronRight } from "lucide-react";
import { GOLD } from "../constants/theme";
import { useWatched } from "../hooks/useWatched";
import { DrillTheater } from "./DrillTheater";

const ff = "'DM Sans', sans-serif";
const hf = "'Bebas Neue', sans-serif";

const norm = (t) => String(t || "").trim().toLowerCase();

/* Mux grabs frame zero by default, and on these clips that is mid-sentence
   with a burned-in caption across the picture. `time` moves the grab; a
   module sets it per drill in `posterTime` because the clean second is
   different in every clip and cannot be guessed from here. */
const poster = (id, w = 640, time) =>
  `https://image.mux.com/${id}/thumbnail.jpg?width=${w}&height=${Math.round(w * 9 / 16)}&fit_mode=smartcrop${
    time != null ? `&time=${time}` : ""}`;
/* The moving version of the same frame. Mux generates it on demand, so this
   costs nothing to ship and nothing to store. */
const motion = (id, w = 480) =>
  `https://image.mux.com/${id}/animated.webp?width=${w}&fps=15&end=6`;

/* Three dots rather than a number, because "2" invites a coach to read it as
   a grade on the drill. Three dots read as how much it will take out of the
   group, which is what it actually is. */
/* mm:ss. The drills table stores seconds on some rows and a string on
   others, so both are handled and anything else is simply not shown. */
function fmtDuration(d) {
  if (d == null || d === "") return null;
  if (typeof d === "string" && d.includes(":")) return d;
  const n = Number(d);
  if (!Number.isFinite(n) || n <= 0) return null;
  const m = Math.floor(n / 60);
  const s = Math.round(n % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function Intensity({ level = 1, C }) {
  const n = Math.max(1, Math.min(3, Number(level) || 1));
  return (
    <span title={`Intensity ${n} of 3`} aria-label={`Intensity ${n} of 3`}
      style={{ display: "inline-flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
      {[1, 2, 3].map(i => (
        <span key={i} style={{ width: 5, height: 5, borderRadius: "50%",
          background: i <= n ? GOLD : (C?.border || "rgba(255,255,255,0.14)") }}/>
      ))}
    </span>
  );
}

export function DrillTile({ d, intensity, watched, onPlay, C, posterTime, note }) {
  const dur = fmtDuration(d.duration);
  return (
    <button type="button" className="btn" onClick={onPlay}
      style={{ textAlign: "left", padding: 0, borderRadius: 12, overflow: "hidden",
        border: `1px solid ${watched ? C.border : GOLD + "33"}`, background: C.cardGradient,
        cursor: "pointer", fontFamily: ff, color: C.text, display: "flex",
        flexDirection: "column", opacity: watched ? 0.62 : 1,
        transition: "opacity .16s ease, border-color .16s ease" }}>
      <div style={{ position: "relative", aspectRatio: "16/9", background: "#000" }}>
        {d.mux_playback_id && (
          <img
            /* Watched cards move. A dimmed still reads as disabled, and this
               one is not disabled, it is just already seen. */
            src={watched ? motion(d.mux_playback_id) : poster(d.mux_playback_id, 640, posterTime)}
            alt="" loading="lazy"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover" }}/>
        )}
        <div style={{ position: "absolute", inset: 0,
          background: "linear-gradient(transparent 52%, rgba(0,0,0,0.68))" }}/>
        {watched && (
          <span style={{ position: "absolute", top: 7, left: 8, display: "inline-flex",
            alignItems: "center", gap: 4, fontSize: 9, fontWeight: 800, letterSpacing: .8,
            textTransform: "uppercase", color: "#111", background: GOLD,
            padding: "2px 7px", borderRadius: 8 }}>
            <Check size={9} strokeWidth={3}/> Seen
          </span>
        )}
        {!watched && (
          <span style={{ position: "absolute", bottom: 7, right: 7, width: 27, height: 27,
            borderRadius: "50%", background: GOLD, display: "flex", alignItems: "center",
            justifyContent: "center" }}>
            <Play size={12} color="#111" fill="#111" style={{ marginLeft: 2 }}/>
          </span>
        )}
        {/* Every card carries something, not just the watched ones. */}
        {dur && (
          <span style={{ position: "absolute", bottom: 7, left: 8, fontSize: 10,
            fontWeight: 700, color: "#FFFFFF", background: "rgba(0,0,0,0.6)",
            padding: "2px 6px", borderRadius: 6, fontVariantNumeric: "tabular-nums" }}>
            {dur}
          </span>
        )}
      </div>
      <div style={{ padding: "9px 11px 11px", display: "flex", alignItems: "flex-start", gap: 8 }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, lineHeight: 1.35 }}>
          {d.title}
          {/* Where else this drill lives, when a page shows it twice. */}
          {note && (
            <span style={{ display: "block", marginTop: 3, fontSize: 10.5, fontWeight: 600,
              color: C.textDim }}>{note}</span>
          )}
        </span>
        {intensity != null && <Intensity level={intensity} C={C}/>}
      </div>
    </button>
  );
}

export function SkillTabs({ intro, qualities = [], intensity = {}, posterTime = {}, echo, C, dark, fetchDrills, drillCache }) {
  const [active, setActive] = useState(qualities[0]?.id || null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(null);
  const { watched } = useWatched();

  const allTitles = useMemo(
    () => [...new Set(qualities.flatMap(q => q.titles || []))],
    [qualities]
  );

  /* One fetch for every drill across every tab, not one per tab. Switching
     tabs after that is free, which is the whole point of tabs. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await fetchDrills(allTitles); } catch { /* the tab shows its empty state */ }
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [allTitles, fetchDrills]);

  const q = qualities.find(x => x.id === active) || qualities[0];
  const drills = ready && q
    ? (q.titles || []).map(t => drillCache.get(norm(t))).filter(Boolean)
    : [];

  // How many of this quality's drills they have already seen, so a coach can
  // tell at a glance which tabs are new ground.
  const seenIn = (quality) => (quality.titles || [])
    .map(t => drillCache.get(norm(t)))
    .filter(d => d && watched.has(d.id)).length;

  return (
    <div style={{ margin: "26px 0", fontFamily: ff }}>
      {intro && (
        <div style={{ fontSize: 15, lineHeight: 1.7, color: C.textMid, marginBottom: 20,
          maxWidth: "70ch" }}>
          {intro}
        </div>
      )}

      <div className="bam-chip-row" style={{ display: "flex", gap: 8, flexWrap: "wrap",
        marginBottom: 16 }}>
        {qualities.map(x => {
          const on = x.id === (q && q.id);
          const seen = ready ? seenIn(x) : 0;
          const total = (x.titles || []).length;
          return (
            <button key={x.id} type="button" className="btn"
              onClick={() => { setActive(x.id); setPlaying(null); }}
              aria-current={on ? "true" : undefined}
              style={{ padding: "9px 15px", borderRadius: 999, fontSize: 12.5, fontWeight: 800,
                letterSpacing: .2, cursor: "pointer", fontFamily: ff,
                border: `1px solid ${on ? GOLD : C.border}`,
                background: on ? `${GOLD}1c` : "transparent",
                color: on ? GOLD : C.textMid, display: "inline-flex", alignItems: "center",
                gap: 7, transition: "all .15s" }}>
              {x.label}
              <span style={{ fontSize: 11, fontWeight: 700, opacity: .65,
                fontVariantNumeric: "tabular-nums", display: "inline-flex",
                alignItems: "center", gap: 3 }}>
                {seen > 0 && <Check size={10} strokeWidth={3}/>}
                {seen > 0 ? `${seen}/${total}` : total}
              </span>
            </button>
          );
        })}
      </div>

      {q && (
        <>
          <div style={{ fontSize: 13.5, color: C.textMid, lineHeight: 1.65, marginBottom: 12,
            paddingLeft: 13, borderLeft: `2px solid ${GOLD}55`, maxWidth: "68ch" }}>
            {q.blurb}
          </div>

          {/* Three gold dots on a card mean nothing without this. */}
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14,
            fontSize: 11, color: C.textDim }}>
            <Intensity level={3} C={C}/>
            <span>Intensity of the challenge, not how advanced the drill is.</span>
          </div>

          {!ready ? (
            <div style={{ fontSize: 13, color: C.textDim, padding: "24px 0" }}>Loading the clips…</div>
          ) : drills.length === 0 ? (
            <div style={{ fontSize: 13, color: C.textDim, padding: "24px 0", fontStyle: "italic" }}>
              These clips are not available yet.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12,
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
              {drills.map(d => (
                <DrillTile key={d.id} d={d} C={C}
                  intensity={intensity[norm(d.title)] ?? intensity[d.title] ?? 1}
                  watched={watched.has(d.id)}
                  posterTime={posterTime[norm(d.title)] ?? posterTime[d.title]}
                  onPlay={() => setPlaying(d)}/>
              ))}
            </div>
          )}
        </>
      )}

      {/* Repeated at the end of the drills because this is where a coach who
          has watched them and is still stuck actually is. A header button
          only helps someone who remembers it is up there. */}
      {echo && ready && (
        <button type="button" onClick={echo.onOpen}
          style={{ marginTop: 18, width: "100%", textAlign: "left", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 12, padding: "14px 16px",
            borderRadius: 12, fontFamily: ff,
            border: `1px solid ${GOLD}44`, background: `${GOLD}0E` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: GOLD, marginBottom: 3 }}>
              {echo.label}
            </div>
            <div style={{ fontSize: 12.5, color: C.textMid, lineHeight: 1.5 }}>
              Players stuck on the same solutions, or you want to add patterns to the toolbox.
            </div>
          </div>
          <ChevronRight size={16} color={GOLD} style={{ flexShrink: 0 }}/>
        </button>
      )}

      {/* One big layer over the page, the same player a drill opens in
          everywhere else, and the thing that writes the watched row. */}
      <DrillTheater drill={playing} C={C} dark={dark} onClose={() => setPlaying(null)}/>
    </div>
  );
}

export default SkillTabs;
